import React, { Component, ErrorInfo, ReactNode } from 'react';
import { isStaleChunkError, isBuildStale, requestReload, isRecoveryExhausted } from '../../utils/chunkRecovery';
import { getSentrySync } from '../../services/monitoring/lazySentry';
import { NewVersionAvailable } from './NewVersionAvailable';

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Wraps the whole app at the React root. Unlike Sentry.ErrorBoundary, this
 * doesn't require the Sentry SDK to be loaded before the first render — Sentry
 * is loaded lazily off the boot path (see services/monitoring/lazySentry.ts)
 * and reporting here is a best-effort call that no-ops until it's ready.
 */
export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  state: RootErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    getSentrySync()?.captureException(error, {
      extra: { componentStack: info.componentStack },
      tags: { errorBoundary: 'root' },
    });
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { error } = this.state;

    // Stale chunk after a deploy → silent reload instead of an error screen.
    // Falls through to the normal error UI below when reload is a no-op
    // (Electron — no deploy skew) or once the reload budget is exhausted.
    if (error && (isStaleChunkError(error) || isBuildStale())) {
      if (requestReload('rootErrorBoundary')) return null;
      if (isRecoveryExhausted()) return <NewVersionAvailable fullScreen />;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px', background: 'var(--bg-primary, #0f0f0f)', color: 'var(--text-primary, #e5e5e5)', fontFamily: 'var(--font-sans, system-ui, sans-serif)' }}>
        <p style={{ margin: 0, fontSize: '15px', fontWeight: 500 }}>Something went wrong</p>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-tertiary, #6b7280)', maxWidth: '400px', textAlign: 'center' }}>{error?.message}</p>
        <button onClick={this.reset} style={{ marginTop: '8px', padding: '6px 16px', fontSize: '13px', cursor: 'pointer', borderRadius: '6px', border: '1px solid var(--theme-border-strong, #d1d5db)', background: 'var(--bg-secondary, #1a1a1a)', color: 'var(--text-primary, #e5e5e5)' }}>
          Try again
        </button>
      </div>
    );
  }
}
